/**
 * PDP2 Product Hero Section – Interactive JS
 *
 * Handles:
 *  1. Swiper init (main carousel + thumbnail sync)
 *  2. Size selector toggle
 *  3. Bundle tier selection (updates price display + variant ID)
 *  4. Per-pillow size selectors for multi-pillow tiers
 *  5. Add-on checkboxes
 *  6. Add to Cart → AJAX /cart/add.js → dispatch ajaxProduct:added → side cart opens
 *  7. Accordion expand/collapse
 */

import Swiper from "swiper";
import { Navigation, Thumbs } from "swiper/modules";

Swiper.use([Navigation, Thumbs]);

(function () {
  const section = document.querySelector("[data-pdp2-hero-section]");
  if (!section) return;

  // -------------------------------------------------------
  // 1. Swiper: Main + Thumbs
  // -------------------------------------------------------
  let thumbSwiper = null;

  const thumbEl = section.querySelector("[data-pdp2-thumb-carousel]");
  const mainEl = section.querySelector("[data-pdp2-main-carousel]");

  if (thumbEl) {
    thumbSwiper = new Swiper(thumbEl, {
      slidesPerView: 5,
      spaceBetween: 8,
      watchSlidesProgress: true,
      breakpoints: {
        0: { slidesPerView: 4, spaceBetween: 6 },
        769: { slidesPerView: 5, spaceBetween: 8 },
      },
    });
  }

  if (mainEl) {
    const slideCount = mainEl.querySelectorAll(".swiper-slide").length;
    // eslint-disable-next-line no-unused-vars
    const mainSwiper = new Swiper(mainEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: slideCount > 2,
      autoHeight: true,
      observer: true,
      observeParents: true,
      navigation: {
        prevEl: section.querySelector("[data-pdp2-main-prev]"),
        nextEl: section.querySelector("[data-pdp2-main-next]"),
      },
      thumbs: thumbSwiper ? { swiper: thumbSwiper } : undefined,
    });
  }

  // -------------------------------------------------------
  // 2. Size Selector (global default size)
  // -------------------------------------------------------
  let selectedSize = "1";
  const sizeBtns = section.querySelectorAll("[data-size]");

  function updateAddonPrices() {
    let checkboxes = section.querySelectorAll(".pdp2-hero__addon-checkbox");
    checkboxes.forEach(function (cb) {
      let label = cb.closest(".pdp2-hero__addon");
      if (!label) return;
      let priceEl = label.querySelector("[data-pdp2-addon-price]");
      let compareEl = label.querySelector("[data-pdp2-addon-compare]");
      let price =
        cb.getAttribute("data-addon-price-size-" + selectedSize) || "";
      let compare =
        cb.getAttribute("data-addon-compare-size-" + selectedSize) || "";
      if (priceEl) priceEl.textContent = price;
      if (compareEl) {
        compareEl.textContent = compare;
        compareEl.style.display = compare ? "" : "none";
      }
    });
  }

  function updatePerPillowDefaults() {
    // Set all per-pillow selectors to match the global selectedSize
    let allPillowBtns = section.querySelectorAll("[data-pillow-size-btn]");
    allPillowBtns.forEach(function (btn) {
      btn.classList.remove("is-active");
      if (btn.getAttribute("data-pillow-size") === selectedSize) {
        btn.classList.add("is-active");
      }
    });
  }

  sizeBtns.forEach(function (btn) {
    btn.addEventListener("click", function () {
      sizeBtns.forEach(function (b) {
        b.classList.remove("is-active");
      });
      btn.classList.add("is-active");
      selectedSize = btn.getAttribute("data-size");
      updateAddonPrices();
      // Also update per-pillow selectors to match new global size
      updatePerPillowDefaults();
    });
  });

  // -------------------------------------------------------
  // 3. Bundle Tier Selection
  // -------------------------------------------------------
  const tiers = section.querySelectorAll("[data-pdp2-tier]");
  const priceCurrentEl = section.querySelector("[data-pdp2-price-current]");
  const priceSavingsEl = section.querySelector("[data-pdp2-price-savings]");

  function selectTier(tier) {
    tiers.forEach(function (t) {
      t.classList.remove("is-selected");
      // Hide per-pillow selectors for non-selected tiers
      let pillowSizes = t.querySelector("[data-pdp2-per-pillow-sizes]");
      if (pillowSizes) pillowSizes.style.display = "none";
    });
    tier.classList.add("is-selected");

    // Show per-pillow selectors for selected tier (if quantity > 1)
    let pillowSizes = tier.querySelector("[data-pdp2-per-pillow-sizes]");
    let qty = parseInt(tier.getAttribute("data-quantity"), 10) || 1;
    if (pillowSizes && qty > 1) {
      pillowSizes.style.display = "";
    }

    // Update price display
    const currentPrice = tier.getAttribute("data-price-current") || "";
    const savings = tier.getAttribute("data-savings") || "";

    if (priceCurrentEl) priceCurrentEl.textContent = currentPrice;
    if (priceSavingsEl) {
      priceSavingsEl.textContent = savings;
      priceSavingsEl.style.display = savings ? "" : "none";
    }
  }

  tiers.forEach(function (tier) {
    tier.addEventListener("click", function (e) {
      // Don't trigger tier select when clicking per-pillow size buttons
      if (e.target.closest("[data-pdp2-per-pillow-sizes]")) return;
      selectTier(tier);
    });
  });

  // -------------------------------------------------------
  // 4. Per-Pillow Size Selectors
  // -------------------------------------------------------
  // Bind per-pillow size button clicks
  section.addEventListener("click", function (e) {
    let btn = e.target.closest("[data-pillow-size-btn]");
    if (!btn) return;

    let group = btn.closest("[data-pillow-index]");
    if (!group) return;

    // Toggle active within this pillow group
    group.querySelectorAll("[data-pillow-size-btn]").forEach(function (b) {
      b.classList.remove("is-active");
    });
    btn.classList.add("is-active");
  });

  // -------------------------------------------------------
  // 5. Get selected variant + addons
  // -------------------------------------------------------
  function getSelectedTier() {
    return section.querySelector("[data-pdp2-tier].is-selected");
  }

  function buildCartItems() {
    let tier = getSelectedTier();
    if (!tier) return [];

    let qty = parseInt(tier.getAttribute("data-quantity"), 10) || 1;
    let items = [];

    if (qty <= 1) {
      // Single pillow tier: use global size selector
      let variantId = tier.getAttribute("data-variant-size-" + selectedSize);
      if (variantId) {
        items.push({
          id: parseInt(variantId, 10),
          quantity: 1,
        });
      }
    } else {
      // Multi-pillow tier: use per-pillow size selectors
      let pillowGroups = tier.querySelectorAll("[data-pillow-index]");
      if (pillowGroups.length > 0) {
        pillowGroups.forEach(function (group) {
          let activeBtn = group.querySelector(
            "[data-pillow-size-btn].is-active",
          );
          let pillowSize = activeBtn
            ? activeBtn.getAttribute("data-pillow-size")
            : selectedSize;
          let varId = tier.getAttribute("data-variant-size-" + pillowSize);
          if (varId) {
            items.push({
              id: parseInt(varId, 10),
              quantity: 1,
            });
          }
        });
      } else {
        // Fallback: no per-pillow selectors, use global size × quantity
        let fallbackId = tier.getAttribute("data-variant-size-" + selectedSize);
        if (fallbackId) {
          items.push({
            id: parseInt(fallbackId, 10),
            quantity: qty,
          });
        }
      }
    }

    return items;
  }

  function getAddonItems() {
    let items = [];
    let checkboxes = section.querySelectorAll(
      ".pdp2-hero__addon-checkbox:checked",
    );
    checkboxes.forEach(function (cb) {
      let variantId = cb.getAttribute(
        "data-addon-variant-size-" + selectedSize,
      );
      let addonQty = parseInt(cb.getAttribute("data-addon-quantity"), 10) || 1;
      if (variantId) {
        items.push({
          id: parseInt(variantId, 10),
          quantity: addonQty,
        });
      }
    });
    return items;
  }

  // -------------------------------------------------------
  // 6. Add to Cart → AJAX → Side Cart (NOT checkout)
  // -------------------------------------------------------
  const buyBtn = section.querySelector("[data-pdp2-add-to-cart]");
  if (buyBtn) {
    buyBtn.addEventListener("click", function () {
      let mainItems = buildCartItems();

      if (mainItems.length === 0) {
        // eslint-disable-next-line no-alert
        alert(
          "Please configure variant IDs in the theme editor for this bundle tier.",
        );
        return;
      }

      // Add any checked add-ons
      let addonItems = getAddonItems();
      let items = mainItems.concat(addonItems);

      // Disable button during request
      buyBtn.disabled = true;
      let originalText =
        buyBtn.dataset.originalText || buyBtn.textContent.trim();
      buyBtn.textContent = "ADDING...";

      let cartAddUrl =
        (window.theme && window.theme.routes && window.theme.routes.cartAdd) ||
        "/cart/add.js";

      fetch(cartAddUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: items }),
      })
        .then(function (response) {
          if (!response.ok) {
            throw new Error("Cart add failed: " + response.status);
          }
          return response.json();
        })
        .then(function (product) {
          // Dispatch event — theme.js listens for this and opens side cart
          document.dispatchEvent(
            new CustomEvent("ajaxProduct:added", {
              detail: {
                product: product,
                addToCartBtn: buyBtn,
              },
            }),
          );

          // Reset button (side cart opens via the event above)
          buyBtn.disabled = false;
          buyBtn.textContent = originalText;
        })
        .catch(function (err) {
          console.error("PDP2 Product: Cart add error", err);
          buyBtn.disabled = false;
          buyBtn.textContent = originalText;
          // eslint-disable-next-line no-alert
          alert("Something went wrong. Please try again.");
        });
    });

    // Store original text
    buyBtn.dataset.originalText = buyBtn.textContent.trim();
  }

  // -------------------------------------------------------
  // 7. Reviews Carousel (below gallery)
  // -------------------------------------------------------
  const reviewsCarousel = section.querySelector(
    "[data-pdp2-reviews-carousel]",
  );
  if (reviewsCarousel) {
    let reviewSlides = reviewsCarousel.querySelectorAll(
      "[data-pdp2-review-slide]",
    );
    let reviewIndex = 0;
    let counterEl = reviewsCarousel.querySelector(
      "[data-pdp2-reviews-counter]",
    );
    let prevBtn = reviewsCarousel.querySelector("[data-pdp2-reviews-prev]");
    let nextBtn = reviewsCarousel.querySelector("[data-pdp2-reviews-next]");

    let showReview = function (index) {
      reviewSlides.forEach(function (slide) {
        slide.classList.remove("is-active");
      });
      reviewSlides[index].classList.add("is-active");
      if (counterEl) {
        counterEl.textContent = index + 1 + " / " + reviewSlides.length;
      }
    };

    if (prevBtn) {
      prevBtn.addEventListener("click", function () {
        reviewIndex =
          (reviewIndex - 1 + reviewSlides.length) % reviewSlides.length;
        showReview(reviewIndex);
      });
    }

    if (nextBtn) {
      nextBtn.addEventListener("click", function () {
        reviewIndex = (reviewIndex + 1) % reviewSlides.length;
        showReview(reviewIndex);
      });
    }
  }

  // -------------------------------------------------------
  // 8. Accordion Expand/Collapse
  // -------------------------------------------------------
  let accordionTriggers = section.querySelectorAll(
    "[data-pdp2-accordion-trigger]",
  );
  accordionTriggers.forEach(function (trigger) {
    trigger.addEventListener("click", function () {
      let accordion = trigger.closest("[data-pdp2-accordion]");
      if (!accordion) return;

      let isOpen = accordion.classList.contains("is-open");

      // Close all
      section.querySelectorAll("[data-pdp2-accordion]").forEach(function (acc) {
        acc.classList.remove("is-open");
        let icon = acc.querySelector(".pdp2-hero__accordion-icon");
        if (icon) icon.textContent = "+";
      });

      // Toggle current
      if (!isOpen) {
        accordion.classList.add("is-open");
        let icon = accordion.querySelector(".pdp2-hero__accordion-icon");
        if (icon) icon.textContent = "\u2212";
      }
    });
  });
})();
